import {
  fetchIsIcorStudyActive,
  fetchDevicesForStudyCreation,
  fetchSentDevicePatientReturned,
  handleMarkDevicePatientReturned,
  checkCanShowModal,
  checkDeviceError,
  getValueAmpEcg,
  handleConvertDeviceData,
  type Device,
  type DeviceOption,
  DEVICE_STATUS,
} from '@/entities/device';
import { fetchFacilityFull, facilitySelection } from '@/entities/facility';
import { useSupportStore } from '@/entities/support';
import { useUserStore } from '@/entities/user';
import {
  handleCreateReferringPhysician,
  usePhysicianStore,
  formatPhysicianOptionLabel,
  referringPhysicianInfoFormatter,
} from '@/entities/physician';
import { DEVICE, EMITTER_CONSTANTS, LOADING_TYPES, ORGANIZATION_SLUG, ROLE } from '@/shared/config';
import {
  PRIMARY_STUDY_TYPE_LIST,
  STUDY_TYPES,
  FOLLOW_ON_STUDY_TYPE,
  STUDY_TYPE_OPTIONS,
  VIRTU_OX_STUDY_TYPE_OPTIONS,
  checkStudyDuration,
} from '@/entities/study';
import { useLoadingStore } from '@/shared/model';
import {
  isWebViewCreateStudy,
  auth,
  emitter,
  showMessageError,
  GETTEXT_ALL_FACILITIES,
} from '@/shared/lib';
import {
  useConstructor,
  useEmitter,
  useMergeState,
  useUpdateEffect,
} from '@/shared/lib/customHooks';
import type { DeviceEventSocketMessage, StudyPricingUpdatedSocketMessage } from '@/shared/lib';
import dayjs, { type Dayjs } from 'dayjs';
import dayjsBusinessDays from 'dayjs-business-days';
import _ from 'lodash';
import React, { useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { t } from 'ttag';
import type {
  Facility,
  FacilityOption,
  NewStudyStep3Props,
  NewStudyStep3State,
  ReferringPhysician,
  Step3DataUpdate,
} from './newStudyStep3.types';
import {
  generateSampleReportPdfData,
  getDeviceModelList,
  handleFetchPhysiciansWithSearch,
} from '../../lib/newStudyStep3';

dayjs.extend(dayjsBusinessDays);

/**
 * Backs step 3 of the new study wizard, owning the facility, device, study type, duration, physician and report due date state plus every handler its form and modals use. Seeds the form from the default data, reloads devices when the model or the assignment mode changes, debounces the device id and physician searches with paging, derives the selectable models, study types and due date warning, reacts to device and pricing socket messages, and exposes step navigation through the forwarded ref.
 *
 * @param defaultData - Step 3 values to seed the form with, including facility, device id, study types and physicians.
 * @param handleClick - Wizard callback invoked with the collected step 3 data and whether the user moved forward.
 * @param updateStepTitleBiofluxDirect - Callback hiding or restoring wizard step 5 for Bioflux Direct and ICOR selections.
 * @param email - Patient email from the earlier step, used to detect the missing-email cases.
 * @param selectedCountry - Patient country from the earlier step, which makes an email mandatory for Biotres outside the US.
 * @param setIsLoadedData - Setter telling the wizard the initial step 3 data has loaded.
 * @param setIsLoading - Setter for the wizard's submit loading flag.
 * @param setIsDisabledNext - Setter that blocks the Next button, used for facilities in arrears.
 * @param setIsLoadingDevice - Setter for the device dropdown's loading flag.
 * @param setIsChangingFacility - Setter for the facility switching flag.
 * @param ref - Forwarded ref exposed with a handleNavigation entry point.
 * @returns Step 3 form state, derived flags and option lists, and the handlers the view binds to.
 */
export const useNewStudyStep3 = (
  {
    defaultData,
    handleClick,
    updateStepTitleBiofluxDirect,
    email = '',
    selectedCountry = '',
    setIsLoadedData,
    setIsLoading,
    setIsDisabledNext,
    setIsLoadingDevice,
    setIsChangingFacility,
  }: NewStudyStep3Props,
  ref: any,
) => {
  /**
   * The support facilities selected from the support store. Back both the facility dropdown options and the arrears check run when a facility is chosen.
   *
   * @returns The support facilities of the signed-in user.
   */
  const supportFacilitiesRedux = useSupportStore((s) => s.supportFacilities);
  /**
   * The signed-in user selected from the user store. Carries the roles and name used to detect clinic users and to default the interpreting physician.
   *
   * @returns The signed-in user record.
   */
  const meRedux = useUserStore((s) => s.me);

  /**
   * The physician store's fetchPhysicians action, selected from usePhysicianStore. It reloads the facility-scoped physician list and discards responses from superseded requests.
   *
   * @returns The store action that refetches a facility's physicians.
   */
  const fetchPhysiciansRequestAction = usePhysicianStore((s) => s.fetchPhysicians);
  /**
   * The loading store's setLoadingText action, selected from useLoadingStore. Used to raise and clear the page loading overlay around the device checks.
   *
   * @returns The store action that sets the page loading text.
   */
  const loadingPageAction = useLoadingStore((s) => s.setLoadingText);

  /**
   * The support facilities from the store mapped to dropdown options and ordered by label ascending. Each option keeps the facility name, id, status and canUseIcor flag, and the list is recomputed only when the store's facilities change.
   *
   * @returns The sorted facility options for the facility dropdown.
   */
  const formattedSupportFacilities = useMemo(
    () =>
      _.orderBy(
        _.map(supportFacilitiesRedux, (supportFacility) => ({
          label: supportFacility.name,
          value: supportFacility.id,
          status: supportFacility.status,
          canUseIcor: supportFacility.canUseIcor,
        })),
        ['label'],
        ['asc'],
      ),
    [supportFacilitiesRedux],
  );

  // Every field below starts empty/unselected ahead of the user's first choice, but
  // NewStudyStep3State models the filled-in shape, so an unknown-cast bridges this initial state.
  const [state, setState] = useMergeState<NewStudyStep3State>({
    newStudyStep3Data: {
      studyType: undefined,
      followOnStudyType: undefined,
      followOnStudyDuration: undefined,
      followOnStudyReportDueDate: undefined,
      isDeviceAssignmentPending: false,
      isTest: false,
      isShouldShowStudyDurationAlert: true,
      isShouldShowFOStudyDurationAlert: true,
      ...defaultData,
      // defaultData starts as {} (an empty ref) until the wizard has real data, so every
      // field below still needs its own blank-slate fallback despite DefaultData requiring it.
      facility: (defaultData.facility ?? null) as unknown as Facility,
      deviceId: defaultData.deviceId ?? null,
      referringPhysician: defaultData.referringPhysician ?? null,
      interprettingPhysician: defaultData.interprettingPhysician ?? null,
      studySettingDuration: (defaultData.studySettingDuration ?? undefined) as unknown as number,
      reportDueDate: defaultData.reportDueDate ?? undefined,
      clinicianInitials: defaultData.clinicianInitials || '',
      isBiofluxDirect: defaultData.isBiofluxDirect ?? false,
      model: defaultData.model || DEVICE.BIOFLUX,
      algAmp: (defaultData.algAmp ?? undefined) as unknown as number,
      unitTime: defaultData.unitTime || 'day',
      unitTimeFO: defaultData.unitTimeFO || 'day',
    },
    referringPhysicians: [],
    interprettingPhysicians: [],
    referringPhysician: null,
    studyType: null,
    primaryStudyType: PRIMARY_STUDY_TYPE_LIST[0],
    reportDueDate: undefined,
    isSearchingDevice: false,
    searchDeviceId: '',
    devices: [],
    devicesOptions: [],
    deviceId: null,
    isDefaultDeviceOptions: true,
    interprettingPhysician: null,
    studySettingDuration: undefined,
    followOnStudyType: null,
    followOnStudyDuration: undefined,
    pdfData: undefined,
    errorObject: {},
    isVisibleDrawer: false,
    isShowSampleReport: false,
    isAllowedUpdate: false,
    sampleReportName: '',
    isDeviceAssignmentPending: false,
    isBiofluxDirect: false,
    formattedSupportFacilities,
    selectedFacility: formattedSupportFacilities[0],
    isOpenTMobileModal: false,
    isOpenFunctionalTestModal: false,
    isNotPassing: false,
    isNewTMobileSimCard: false,
    isCheckingDeviceStatus: false,
    isShowMoreInfo: false,
    isOpenPatientEmailModal: false,
    isTest: false,
    isOpenProtectPlusDeviceModal: false,
    isCreatingReferringPhysician: false,
    isOpenWarningModal: false,
    isOpenWarningAutoEventModal: false,
    unitTime: undefined,
    unitTimeFO: undefined,
    isShowedStudyDurationAlert: false,
    isShowedFOStudyDurationAlert: false,
    isOpenReturnedDeviceModal: false,
    isSavingReturnedDeviceModal: false,
    returnedDeviceDataModal: {},
    isOpenDeviceErrorModal: false,
    deviceErrorModalMessage: '',
    // *: New state for physician search with debounce and pagination
    isLoadingReferringPhysician: false,
    searchReferringPhysician: '',
    referringPhysiciansOptions: [],
    hasMoreReferringPhysicians: true,
    isLoadingInterprettingPhysician: false,
    searchInterprettingPhysician: '',
    interprettingPhysiciansOptions: [],
    hasMoreInterprettingPhysicians: true,
  } as unknown as NewStudyStep3State);

  const facilityStatus = useRef<string | null>(null);
  const fetchDeviceTimeout = useRef<NodeJS.Timeout | undefined>(undefined);
  const hasClosedDeviceIdDropdown = useRef(false);
  const hasChangedDevice = useRef(false);
  const deviceIdSelectRef = useRef<{ focusAndOpenSelectInput: () => void } | null>(null);
  const searchPhysicianTimeouts = useRef<{
    referring: NodeJS.Timeout | undefined;
    interpreting: NodeJS.Timeout | undefined;
  }>({
    referring: undefined,
    interpreting: undefined,
  });
  const lastScrollTop = useRef<{ referring: number; interpreting: number }>({
    referring: 0,
    interpreting: 0,
  });
  const isCardioOne = auth.userOrganization()?.slug === ORGANIZATION_SLUG.CARDIO_ONE;

  /**
   * The device models selectable for the current facility. Recomputed by getDeviceModelList from the functional-test flag, the Bioflux Direct flag and the facility's canUseIcor permission whenever any of them changes.
   *
   * @returns The list of selectable device model options.
   */
  const deviceModelList = useMemo(
    () =>
      getDeviceModelList({
        isTest: state.isTest,
        isBiofluxDirect: state.isBiofluxDirect,
        canUseIcor: state.selectedFacility?.canUseIcor,
      }),
    [state.selectedFacility, state.isTest, state.isBiofluxDirect],
  );

  /**
   * Whether the selected device model is ICOR. Memoized on the model held in the step 3 data.
   *
   * @returns True when the ICOR model is selected.
   */
  const isIcorDevice = useMemo(
    () => state.newStudyStep3Data.model === DEVICE.ICOR,
    [state.newStudyStep3Data.model],
  );

  /**
   * The study type options the form offers. Returns the VirtuOx set when an ICOR device is selected and the device assignment is not deferred, otherwise the full STUDY_TYPE_OPTIONS.
   *
   * @returns The study type options to display.
   */
  const studyTypeOptions = useMemo(() => {
    if (isIcorDevice && !state.isDeviceAssignmentPending) {
      return VIRTU_OX_STUDY_TYPE_OPTIONS;
    }

    return STUDY_TYPE_OPTIONS;
  }, [isIcorDevice, state.isDeviceAssignmentPending]);

  const isWebView = isWebViewCreateStudy();

  /**
   * Whether the primary study of the form is a Holter study. True when the selected study type is Holter, or it is the follow-on sentinel while the primary study type in the step 3 data is Holter.
   *
   * @returns True when the primary study is a Holter study.
   */
  const isFirstHolterStudy =
    state.studyType?.value === STUDY_TYPES.HOLTER ||
    (state.studyType?.value === FOLLOW_ON_STUDY_TYPE &&
      state.newStudyStep3Data.studyType === STUDY_TYPES.HOLTER);

  const isFollowOnStudyType = state.studyType?.value === FOLLOW_ON_STUDY_TYPE;
  /**
   * Whether the duration unit selector may be used for the study currently configured in step 3. True when the step 3 study type is Holter, either on its own or while a follow-on study type is selected.
   *
   * @returns True when the Holter study type allows picking a duration unit.
   */
  const canSelectDurationUnit =
    state.newStudyStep3Data.studyType === STUDY_TYPES.HOLTER ||
    (isFollowOnStudyType && state.newStudyStep3Data.primaryStudyType === STUDY_TYPES.HOLTER);
  const isSecondHolterStudy = state.newStudyStep3Data.followOnStudyType === STUDY_TYPES.HOLTER;

  const isHolterFOStudyReport = state.followOnStudyType?.value === STUDY_TYPES.HOLTER;

  const isMissingEmailWhenUseBiodirect = state.isBiofluxDirect && !email;
  /**
   * Whether the patient email is required but missing for the current device and country. True when no email was entered, the selected model is a Biotres variant, and the selected country is not US.
   *
   * @returns True when a patient email is missing for a Biotres device outside the US.
   */
  const isMissingEmailWhenDeviceBiotresAndOutsideUS =
    !email && _.includes(state.newStudyStep3Data.model, DEVICE.BIOTRES) && selectedCountry !== 'US';

  /**
   * Shows or hides the drawer used to create a referring physician. Flips isVisibleDrawer in state.
   *
   * @returns Nothing.
   */
  const toggleDisplayCreateReferringPhysicianDrawer = () => {
    setState({ isVisibleDrawer: !state.isVisibleDrawer });
  };

  /**
   * Shows or hides the sample report preview. When closing it marks the device as programmatically changed so the device id search that follows is skipped, then writes the new isShowSampleReport flag.
   *
   * @returns Nothing.
   */
  const toggleDisplaySampleReport = () => {
    const updatedShowSampleReport = !state.isShowSampleReport;
    if (!updatedShowSampleReport) {
      hasChangedDevice.current = true;
    }
    setState({ isShowSampleReport: updatedShowSampleReport });
  };

  /**
   * Dismisses the short study duration alert. Clears isShouldShowStudyDurationAlert on a copy of the step 3 data and lowers isShowedStudyDurationAlert.
   *
   * @returns Nothing.
   */
  const handleCloseStudyDurationAlert = () => {
    const clonedNewStudyStep3Data = { ...state.newStudyStep3Data };
    clonedNewStudyStep3Data.isShouldShowStudyDurationAlert = false;
    setState({
      newStudyStep3Data: clonedNewStudyStep3Data,
      isShowedStudyDurationAlert: false,
    });
  };

  /**
   * Dismisses the short follow-on study duration alert. Clears isShouldShowFOStudyDurationAlert on a copy of the step 3 data and lowers isShowedFOStudyDurationAlert.
   *
   * @returns Nothing.
   */
  const handleCloseFOStudyDurationAlert = () => {
    const clonedNewStudyStep3Data = { ...state.newStudyStep3Data };
    clonedNewStudyStep3Data.isShouldShowFOStudyDurationAlert = false;
    setState({
      newStudyStep3Data: clonedNewStudyStep3Data,
      isShowedFOStudyDurationAlert: false,
    });
  };

  /**
   * Produces the sample report PDF for either the primary or the follow-on study type, depending on which field was clicked. Opens the generated URL in a new tab inside the web view, otherwise stores the PDF data with the study type's label and shows the in-page preview.
   *
   * @param isClickStudyType - True to use the primary study type, false for the follow-on one.
   * @returns Nothing.
   */
  const downloadSampleReport = (isClickStudyType = false) => {
    const studyType = isClickStudyType
      ? isFollowOnStudyType
        ? state.newStudyStep3Data.primaryStudyType
        : state.newStudyStep3Data.studyType
      : state.newStudyStep3Data.followOnStudyType;
    const pdfData = generateSampleReportPdfData(studyType);
    if (isWebView) {
      window.open(pdfData?.url);
    } else {
      /**
       * The study type option matching the study type in the step 3 data. Looked up in STUDY_TYPE_OPTIONS by value and used as the sample report's display name.
       *
       * @returns The matching study type option, or undefined.
       */
      const studyTypeOption = STUDY_TYPE_OPTIONS.find(
        (x) => x.value === state.newStudyStep3Data.studyType,
      );
      /**
       * The study type option matching the follow-on study type in the step 3 data. Looked up in STUDY_TYPE_OPTIONS by value and used as the sample report's display name.
       *
       * @returns The matching follow-on study type option, or undefined.
       */
      const followOnStudyTypeOption = STUDY_TYPE_OPTIONS.find(
        (x) => x.value === state.newStudyStep3Data.followOnStudyType,
      );
      setState({
        pdfData,
        isShowSampleReport: true,
        sampleReportName: isClickStudyType
          ? studyTypeOption?.label
          : followOnStudyTypeOption?.label,
      });
    }
  };

  /**
   * Sets the report due date, and the follow-on report due date, on the working step 3 data from the study durations and the facility's study setting. Normalises each duration to whole days (falling back to the facility duration or one day), then adds 3 or 4 business days to a follow-on primary study and 5 or 6 otherwise depending on whether the next day is a business day, filling only empty dates when isInitial is set and returning early when updating is suppressed.
   *
   * @param updatedNewStudyStep3Data - Working step 3 data that is mutated in place.
   * @param facility - Facility whose study setting supplies the fallback duration.
   * @param isInitial - When true, existing due dates are kept instead of overwritten.
   * @returns Nothing.
   */
  const updateReportDueDate = (
    updatedNewStudyStep3Data: Step3DataUpdate,
    facility: Facility,
    isInitial = false,
  ) => {
    if (updatedNewStudyStep3Data.preventUpdatingReportDueDate) {
      return;
    }

    const { studySetting } = facility;

    const isFollowOnStudy = !_.isNil(updatedNewStudyStep3Data.followOnStudyType);

    const isUnitDays = updatedNewStudyStep3Data.unitTime === 'day';
    const isUnitDaysFO = updatedNewStudyStep3Data.unitTimeFO === 'day';
    let studySettingDuration = Math.floor(
      updatedNewStudyStep3Data.studySettingDuration > 0
        ? updatedNewStudyStep3Data.studySettingDuration / (isUnitDays ? 1440 : 60)
        : studySetting
          ? studySetting.duration / 1440
          : 1,
    );
    let followOnStudyDuration = isFollowOnStudy
      ? Math.floor(
          (updatedNewStudyStep3Data.followOnStudyDuration ?? 0) > 0
            ? (updatedNewStudyStep3Data.followOnStudyDuration ?? 0) / (isUnitDaysFO ? 1440 : 60)
            : studySetting
              ? studySetting.duration / 1440
              : 1,
        )
      : 0;
    if (!isUnitDays) {
      studySettingDuration = Math.ceil(studySettingDuration / 24);
    }
    if (!isUnitDaysFO) {
      followOnStudyDuration = Math.ceil(followOnStudyDuration / 24);
    }
    const studySettingDurationMomentBusiness = dayjs().add(studySettingDuration, 'days');
    const followOnStudyDurationMomentBusiness = dayjs()
      .add(studySettingDuration, 'days')
      .add(followOnStudyDuration, 'days');
    const isFirstStudyBusinessDay = studySettingDurationMomentBusiness
      .clone()
      .add(1, 'days')
      .isBusinessDay();
    const isSecondStudyBusinessDay = followOnStudyDurationMomentBusiness
      .clone()
      .add(1, 'days')
      .isBusinessDay();
    if (isInitial) {
      if (isFollowOnStudy) {
        updatedNewStudyStep3Data.followOnStudyReportDueDate =
          updatedNewStudyStep3Data.followOnStudyReportDueDate ||
          followOnStudyDurationMomentBusiness.businessDaysAdd(isSecondStudyBusinessDay ? 5 : 6);
        updatedNewStudyStep3Data.reportDueDate =
          updatedNewStudyStep3Data.reportDueDate ||
          studySettingDurationMomentBusiness.businessDaysAdd(isFirstStudyBusinessDay ? 3 : 4);
      } else {
        updatedNewStudyStep3Data.reportDueDate =
          updatedNewStudyStep3Data.reportDueDate ||
          studySettingDurationMomentBusiness.businessDaysAdd(isFirstStudyBusinessDay ? 5 : 6);
      }
    } else if (isFollowOnStudy) {
      updatedNewStudyStep3Data.followOnStudyReportDueDate =
        followOnStudyDurationMomentBusiness.businessDaysAdd(isSecondStudyBusinessDay ? 5 : 6);
      updatedNewStudyStep3Data.reportDueDate = studySettingDurationMomentBusiness.businessDaysAdd(
        isFirstStudyBusinessDay ? 3 : 4,
      );
    } else {
      updatedNewStudyStep3Data.reportDueDate = studySettingDurationMomentBusiness.businessDaysAdd(
        isFirstStudyBusinessDay ? 5 : 6,
      );
    }
  };

  /**
   * Fills the study settings of the working step 3 data from the facility's study setting and returns the matching state patch. Normalises the study and follow-on durations to whole days by their unit (falling back to the facility duration or one day), applies event times, bradycardia and tachycardia thresholds, pause and afib detection and diagnostic lead with Holter types forcing their own values and isDefault overriding entered ones, sets the amplify-ECG flag and recomputes the report due date.
   *
   * @param updatedNewStudyStep3Data - Working step 3 data that is mutated in place.
   * @param facility - Facility supplying the default study setting and amplify-ECG flag.
   * @param isDefault - When true, values already entered are replaced by the defaults.
   * @returns State patch holding the normalised durations, units and report due date.
   */
  const setStudySetting = (
    updatedNewStudyStep3Data: Step3DataUpdate,
    facility: Facility,
    isDefault = false,
  ) => {
    const { studySetting } = facility;

    const { unitTime, unitTimeFO } = updatedNewStudyStep3Data;
    const isUnitDays = unitTime === 'day';
    const isUnitDaysFO = unitTimeFO === 'day';

    const setStateObject = {};

    // For customer environment
    const isFirstHolterStudy = updatedNewStudyStep3Data.studyType === STUDY_TYPES.HOLTER;
    // *: For testing (CP-2.24.1)
    // const isHolterStudyRp = true;

    const isFollowOnStudy = !_.isNil(updatedNewStudyStep3Data.followOnStudyType);
    if (isFollowOnStudy) {
      const followOnStudyDuration = Math.floor(
        (updatedNewStudyStep3Data.followOnStudyDuration ?? 0) > 0
          ? (updatedNewStudyStep3Data.followOnStudyDuration ?? 0) / (isUnitDaysFO ? 1440 : 60)
          : studySetting
            ? studySetting.duration / 1440
            : 1,
      );
      const studySettingDuration = Math.floor(
        updatedNewStudyStep3Data.studySettingDuration > 0
          ? updatedNewStudyStep3Data.studySettingDuration / (isUnitDays ? 1440 : 60)
          : studySetting
            ? studySetting.duration / 1440
            : 1,
      );
      _.assign(setStateObject, {
        followOnStudyDuration,
        studySettingDuration,
        unitTime,
        unitTimeFO,
      });
      updatedNewStudyStep3Data.followOnStudyDuration =
        followOnStudyDuration * (isUnitDaysFO ? 1440 : 60);
      updatedNewStudyStep3Data.studySettingDuration =
        studySettingDuration * (isUnitDays ? 1440 : 60);
    } else if (isFirstHolterStudy) {
      const studySettingDuration = Math.floor(
        updatedNewStudyStep3Data.studySettingDuration > 0
          ? updatedNewStudyStep3Data.studySettingDuration / (isUnitDays ? 1440 : 60)
          : studySetting
            ? studySetting.duration / 1440
            : 1,
      );
      _.assign(setStateObject, { studySettingDuration, unitTime });
      updatedNewStudyStep3Data.studySettingDuration =
        studySettingDuration * (isUnitDays ? 1440 : 60);
    } else {
      const studySettingDuration = Math.floor(
        updatedNewStudyStep3Data.studySettingDuration > 0
          ? updatedNewStudyStep3Data.studySettingDuration / 1440
          : studySetting
            ? studySetting.duration / 1440
            : 1,
      );
      _.assign(setStateObject, { studySettingDuration, unitTime });
      updatedNewStudyStep3Data.studySettingDuration = studySettingDuration * 1440;
    }

    const shouldUseHolterStudySettingsTypes = [STUDY_TYPES.HOLTER, STUDY_TYPES.EXTENDED_HOLTER];
    const shouldUseHolterStudySettings = isFollowOnStudy
      ? shouldUseHolterStudySettingsTypes.includes(updatedNewStudyStep3Data.followOnStudyType ?? '')
      : shouldUseHolterStudySettingsTypes.includes(updatedNewStudyStep3Data.studyType ?? '');

    updatedNewStudyStep3Data.preEventTime =
      !_.isEmpty(updatedNewStudyStep3Data.preEventTime) && !isDefault
        ? updatedNewStudyStep3Data.preEventTime
        : studySetting
          ? studySetting.preEventTime
          : 30;
    updatedNewStudyStep3Data.postEventTime =
      !_.isEmpty(updatedNewStudyStep3Data.postEventTime) && !isDefault
        ? updatedNewStudyStep3Data.postEventTime
        : studySetting
          ? studySetting.postEventTime
          : 30;
    updatedNewStudyStep3Data.bradycardiaThreshold =
      !_.isEmpty(updatedNewStudyStep3Data.bradycardiaThreshold) && !isDefault
        ? updatedNewStudyStep3Data.bradycardiaThreshold
        : shouldUseHolterStudySettings
          ? 1
          : studySetting
            ? studySetting.bradycardiaThreshold
            : 60;
    updatedNewStudyStep3Data.tachycardiaThreshold =
      !_.isEmpty(updatedNewStudyStep3Data.tachycardiaThreshold) && !isDefault
        ? updatedNewStudyStep3Data.tachycardiaThreshold
        : shouldUseHolterStudySettings
          ? 300
          : studySetting
            ? studySetting.tachycardiaThreshold
            : 120;
    updatedNewStudyStep3Data.pauseLevel =
      !_.isEmpty(updatedNewStudyStep3Data.pauseLevel) && !isDefault
        ? updatedNewStudyStep3Data.pauseLevel
        : shouldUseHolterStudySettings
          ? 2.5
          : studySetting
            ? studySetting.pauseLevel
            : 2.5;
    updatedNewStudyStep3Data.pauseDetection =
      !_.isEmpty(updatedNewStudyStep3Data.pauseDetection) && !isDefault
        ? updatedNewStudyStep3Data.pauseDetection
        : shouldUseHolterStudySettings
          ? false
          : studySetting
            ? !!studySetting.pauseDetection
            : true;
    updatedNewStudyStep3Data.afibDetection =
      !_.isEmpty(updatedNewStudyStep3Data.afibDetection) && !isDefault
        ? updatedNewStudyStep3Data.afibDetection
        : shouldUseHolterStudySettings
          ? false
          : studySetting
            ? !!studySetting.afibDetection
            : false;
    updatedNewStudyStep3Data.diagnosticLead =
      !_.isEmpty(updatedNewStudyStep3Data.diagnosticLead) && !isDefault
        ? updatedNewStudyStep3Data.diagnosticLead
        : shouldUseHolterStudySettings
          ? 2
          : studySetting
            ? studySetting.diagnosticLead
            : 2;

    const ampEcgSignal = getValueAmpEcg(facility.amplifyEcgSignal, updatedNewStudyStep3Data.model);
    updatedNewStudyStep3Data.algAmp = ampEcgSignal;

    updateReportDueDate(updatedNewStudyStep3Data, facility, true);
    _.assign(setStateObject, {
      reportDueDate: isFollowOnStudy
        ? updatedNewStudyStep3Data.followOnStudyReportDueDate
        : updatedNewStudyStep3Data.reportDueDate,
    });

    return setStateObject;
  };

  /**
   * Toggles deferring the device assignment of the new study and mirrors the flag into the step 3 data. Enabling it pins the device id to the 0000000000 placeholder and turns off Bioflux Direct, disabling it clears the device id, and it hides wizard step 5 for a deferred or ICOR selection while dropping a study type ICOR cannot run.
   *
   * @returns Nothing.
   */
  const handleAssignDeviceLaterChange = () => {
    const setStateObject = {};
    const updatedNewStudyStep3Data = { ...state.newStudyStep3Data };

    const updatedIsDeviceAssignmentPending = !state.isDeviceAssignmentPending;

    _.assign(setStateObject, { isDeviceAssignmentPending: updatedIsDeviceAssignmentPending });
    updatedNewStudyStep3Data.isDeviceAssignmentPending = updatedIsDeviceAssignmentPending;

    if (updatedIsDeviceAssignmentPending) {
      _.assign(setStateObject, {
        deviceId: '0000000000',
        searchDeviceId: '',
        isBiofluxDirect: false,
      });
      updatedNewStudyStep3Data.deviceId = '0000000000';
      updatedNewStudyStep3Data.isBiofluxDirect = false;
    } else {
      _.assign(setStateObject, {
        deviceId: null,
        searchDeviceId: '',
      });
      updatedNewStudyStep3Data.deviceId = null;
    }

    /**
     * Whether step 5 of the new study wizard must be hidden. True when the device assignment is deferred or the selected model is ICOR.
     *
     * @returns True when step 5 must be hidden.
     */
    const isHideStep5 =
      updatedIsDeviceAssignmentPending || updatedNewStudyStep3Data.model === DEVICE.ICOR;
    updateStepTitleBiofluxDirect(isHideStep5);

    _.assign(setStateObject, { newStudyStep3Data: updatedNewStudyStep3Data });
    if (
      !updatedIsDeviceAssignmentPending &&
      isIcorDevice &&
      !_.some(VIRTU_OX_STUDY_TYPE_OPTIONS, (x) => x.value === state.studyType?.value)
    ) {
      _.assign(setStateObject, {
        studyType: null,
        followOnStudyDuration: null,
        followOnStudyType: null,
      });
    }

    setState(setStateObject);
  };

  /**
   * Toggles Bioflux Direct for the new study and mirrors the flag into the step 3 data. Enabling it switches an ICOR model to Bioflux, pins the device id to the 0000000000 placeholder and clears the deferred device assignment, disabling it clears the device id, and either way the wizard step titles are refreshed.
   *
   * @returns Nothing.
   */
  const handleUseBiodirectChange = () => {
    const setStateObject = {};
    const updatedNewStudyStep3Data = { ...state.newStudyStep3Data };

    const updatedIsBiofluxDirect = !state.isBiofluxDirect;

    _.assign(setStateObject, { isBiofluxDirect: updatedIsBiofluxDirect });
    updatedNewStudyStep3Data.isBiofluxDirect = updatedIsBiofluxDirect;
    if (updatedIsBiofluxDirect && updatedNewStudyStep3Data.model === DEVICE.ICOR) {
      updatedNewStudyStep3Data.model = DEVICE.BIOFLUX;
    }
    updateStepTitleBiofluxDirect(updatedIsBiofluxDirect);

    if (updatedIsBiofluxDirect) {
      _.assign(setStateObject, {
        deviceId: '0000000000',
        searchDeviceId: '',
      });
      updatedNewStudyStep3Data.deviceId = '0000000000';

      _.assign(setStateObject, { isDeviceAssignmentPending: false });
      updatedNewStudyStep3Data.isDeviceAssignmentPending = false;
    } else {
      _.assign(setStateObject, {
        deviceId: null,
        searchDeviceId: '',
      });
      updatedNewStudyStep3Data.deviceId = null;
    }

    _.assign(setStateObject, { newStudyStep3Data: updatedNewStudyStep3Data });

    setState(setStateObject);
  };

  /**
   * Normalises a typed ICOR device id to uppercase alphanumerics. Strips every non-alphanumeric character and uppercases the result only when an ICOR device is selected and the edited field is the device id string, returning the value untouched otherwise.
   *
   * @param fieldName - Name of the step 3 field being edited.
   * @param fieldValue - Value entered for that field.
   * @returns Sanitised uppercase device id, or the original value.
   */
  const formatValueForIcorUppercase = (fieldName: string, fieldValue: string | boolean) => {
    if (isIcorDevice && fieldName === 'deviceId' && typeof fieldValue === 'string') {
      // Only accept alphanumeric values (numbers and alphabet)
      const sanitized = fieldValue.replace(/[^a-zA-Z0-9]/g, '');
      return sanitized.toUpperCase();
    }

    return fieldValue;
  };

  /**
   * Applies a field edit of step 3 to both the working step 3 data and the derived form state. Branches per field: a primary study type resets the unit to days and seeds 1 or 30 day durations for Holter and Extended Holter, a duration is converted to minutes by its unit and may raise the short-duration alerts, Bioflux Direct and model changes swap the device id placeholder and recompute the amplify-ECG value, and ICOR also clears the study types, hides step 5 and uppercases the device id.
   *
   * @param name - Name of the edited step 3 field.
   * @param value - New value for that field.
   * @returns Nothing.
   */
  const onChange = (name: string, value: any) => {
    const updatedNewStudyStep3Data = { ...state.newStudyStep3Data };

    const setStateObject = {};

    switch (name) {
      case 'primaryStudyType': {
        Object.assign(setStateObject, {
          unitTime: 'day',
          [name]: value,
        });
        Object.assign(updatedNewStudyStep3Data, {
          studyType: value,
          unitTime: 'day',
        });
        switch (value) {
          case STUDY_TYPES.HOLTER: {
            Object.assign(setStateObject, { studySettingDuration: 1 });
            Object.assign(updatedNewStudyStep3Data, { studySettingDuration: 1440 });
            break;
          }
          case STUDY_TYPES.EXTENDED_HOLTER: {
            Object.assign(setStateObject, { studySettingDuration: 30 });
            Object.assign(updatedNewStudyStep3Data, { studySettingDuration: 30 * 1440 });
            break;
          }
          default: {
            break;
          }
        }

        updateReportDueDate(updatedNewStudyStep3Data, updatedNewStudyStep3Data.facility);
        _.assign(setStateObject, {
          reportDueDate: updatedNewStudyStep3Data.followOnStudyReportDueDate,
        });

        break;
      }
      case 'studySettingDuration':
      case 'followOnStudyDuration': {
        _.assign(setStateObject, { [name]: value });

        updatedNewStudyStep3Data[name] =
          (name === 'studySettingDuration' && updatedNewStudyStep3Data.unitTime === 'hour') ||
          (name === 'followOnStudyDuration' && updatedNewStudyStep3Data.unitTimeFO === 'hour')
            ? Number(value) * 60
            : Number(value) * 1440;
        updateReportDueDate(updatedNewStudyStep3Data, updatedNewStudyStep3Data.facility);

        const isFollowOnStudy = !_.isNil(updatedNewStudyStep3Data.followOnStudyType);
        if (name === 'studySettingDuration') {
          if (
            !state.isShowedStudyDurationAlert &&
            updatedNewStudyStep3Data.isShouldShowStudyDurationAlert &&
            !isFirstHolterStudy &&
            +value < 10
          ) {
            _.assign(setStateObject, { isShowedStudyDurationAlert: true });
          }
        } else if (
          !state.isShowedFOStudyDurationAlert &&
          updatedNewStudyStep3Data.isShouldShowFOStudyDurationAlert &&
          !isHolterFOStudyReport &&
          +value < 10
        ) {
          _.assign(setStateObject, { isShowedFOStudyDurationAlert: true });
        }
        _.assign(setStateObject, {
          reportDueDate: isFollowOnStudy
            ? updatedNewStudyStep3Data.followOnStudyReportDueDate
            : updatedNewStudyStep3Data.reportDueDate,
        });
        break;
      }
      case 'isBiofluxDirect': {
        _.assign(setStateObject, { [name]: !value });
        updatedNewStudyStep3Data[name] = !value;
        _.assign(setStateObject, {
          deviceId: !value ? '0000000000' : null,
        });
        updatedNewStudyStep3Data.deviceId = !value ? '0000000000' : null;
        updateStepTitleBiofluxDirect(!value);
        break;
      }
      case 'isTest': {
        _.assign(setStateObject, { [name]: value });
        updatedNewStudyStep3Data[name] = value;
        break;
      }
      case 'deviceId': {
        _.assign(setStateObject, { [name]: value });
        if (isIcorDevice) {
          updatedNewStudyStep3Data[name] = formatValueForIcorUppercase(name, value) as string;
        } else {
          updatedNewStudyStep3Data[name] = value;
        }
        break;
      }
      case 'model': {
        const ampEcgSignal = getValueAmpEcg(
          state.newStudyStep3Data.facility.amplifyEcgSignal,
          value,
        );
        _.assign(updatedNewStudyStep3Data, {
          deviceId:
            state.isDeviceAssignmentPending || state.newStudyStep3Data.isBiofluxDirect
              ? '0000000000'
              : null,
          algAmp: ampEcgSignal,
        });
        _.assign(setStateObject, {
          deviceId:
            state.isDeviceAssignmentPending || state.newStudyStep3Data.isBiofluxDirect
              ? '0000000000'
              : null,
          algAmp: ampEcgSignal,
          searchDeviceId: '',
        });
        if (value === DEVICE.ICOR) {
          _.assign(updatedNewStudyStep3Data, {
            studyType: '',
            followOnStudyDuration: null,
            followOnStudyType: null,
          });
          _.assign(setStateObject, {
            studyType: null,
            primaryStudyType: null,
            followOnStudyDuration: null,
            followOnStudyType: null,
          });
          updateStepTitleBiofluxDirect(true);
        } else {
          updateStepTitleBiofluxDirect(false);
        }
        updatedNewStudyStep3Data[name] = value;
        break;
      }
      default: {
        (updatedNewStudyStep3Data as any)[name] = value;
        break;
      }
    }

    _.assign(setStateObject, { newStudyStep3Data: updatedNewStudyStep3Data });

    setState(setStateObject);
  };

  /**
   * Applies a dropdown selection in step 3 and cascades the study settings it implies. Holter clears the follow-on fields and sets 3 days, the follow-on sentinel seeds MCT Peek at 30 days over a one day primary study, other types set 30 days, switching a duration unit seeds 50 hours or the type-specific day count, a Biotres device without auto event detection or with an outdated schema opens the matching warning modal, and the due date and study settings are recomputed.
   *
   * @param name - Name of the dropdown that changed.
   * @param value - Selected value.
   * @param option - Selected option, stored as the field's state value.
   * @returns Nothing.
   */
  const onChangeSelection = (
    name: string,
    value: string,
    option: {
      label: string;
      value: string;
    },
  ) => {
    const setStateObject = {};

    const updatedNewStudyStep3Data = { ...state.newStudyStep3Data };

    (updatedNewStudyStep3Data as any)[name] = option.value;

    switch (name) {
      case 'studyType': {
        if (
          !state.isDeviceAssignmentPending &&
          !state.isBiofluxDirect &&
          state.deviceId &&
          state.newStudyStep3Data.model === DEVICE.BIOTRES &&
          ![STUDY_TYPES.HOLTER, STUDY_TYPES.EXTENDED_HOLTER].includes(option.value)
        ) {
          if (
            !state.deviceId.canRunBiotresMCT &&
            !state.deviceId.isBiotresPro &&
            state.selectedFacility?.status !== 'Test'
          ) {
            _.assign(setStateObject, { isOpenWarningAutoEventModal: true });
          } else if (checkCanShowModal(state.deviceId.schema)) {
            _.assign(setStateObject, { isOpenWarningModal: true });
          }
        }
        if (option.value === STUDY_TYPES.HOLTER) {
          _.assign(setStateObject, {
            followOnStudyDuration: null,
            followOnStudyType: null,
            studySettingDuration: 3,
          });
          _.assign(updatedNewStudyStep3Data, {
            followOnStudyDuration: null,
            followOnStudyReportDueDate: null,
            followOnStudyType: null,
            studySettingDuration: 1440 * 3,
            unitTime: 'day',
          });
        } else if (option.value === FOLLOW_ON_STUDY_TYPE) {
          /**
           * The study type option for MCT Peek, seeded as the default follow-on study type. Found in STUDY_TYPE_OPTIONS when the user picks the follow-on study sentinel.
           *
           * @returns The MCT Peek study type option.
           */
          const defaultFollowOnStudyType = STUDY_TYPE_OPTIONS.find(
            (x) => x.value === STUDY_TYPES.MCT_PEEK,
          );
          _.assign(setStateObject, {
            studySettingDuration: 1,
            primaryStudyType: PRIMARY_STUDY_TYPE_LIST[0],
            followOnStudyType: defaultFollowOnStudyType,
            followOnStudyDuration: 30,
          });
          _.assign(updatedNewStudyStep3Data, {
            studyType: PRIMARY_STUDY_TYPE_LIST[0].value,
            primaryStudyType: PRIMARY_STUDY_TYPE_LIST[0].value,
            studySettingDuration: 1440,
            unitTime: 'day',
            followOnStudyType: defaultFollowOnStudyType?.value,
            followOnStudyDuration: 1440 * 30,
            unitTimeFO: 'day',
          });
        } else {
          _.assign(setStateObject, {
            followOnStudyDuration: null,
            followOnStudyType: null,
            studySettingDuration: 30,
          });
          _.assign(updatedNewStudyStep3Data, {
            followOnStudyDuration: null,
            followOnStudyReportDueDate: null,
            followOnStudyType: null,
            studySettingDuration: 1440 * 30,
            unitTime: 'day',
          });
        }

        updateReportDueDate(updatedNewStudyStep3Data, updatedNewStudyStep3Data.facility);
        const updateSetStateObject = setStudySetting(
          updatedNewStudyStep3Data,
          updatedNewStudyStep3Data.facility,
          true,
        );
        _.assign(setStateObject, updateSetStateObject);

        break;
      }
      case 'primaryStudyType': {
        if (
          !state.isDeviceAssignmentPending &&
          !state.isBiofluxDirect &&
          state.deviceId &&
          state.newStudyStep3Data.model === DEVICE.BIOTRES &&
          ![STUDY_TYPES.HOLTER, STUDY_TYPES.EXTENDED_HOLTER].includes(option.value)
        ) {
          if (
            !state.deviceId.canRunBiotresMCT &&
            !state.deviceId.isBiotresPro &&
            state.selectedFacility?.status !== 'Test'
          ) {
            _.assign(setStateObject, { isOpenWarningAutoEventModal: true });
          } else if (checkCanShowModal(state.deviceId.schema)) {
            _.assign(setStateObject, { isOpenWarningModal: true });
          }
        }
        if (option.value === STUDY_TYPES.HOLTER) {
          _.assign(setStateObject, { studySettingDuration: 1 });
          _.assign(updatedNewStudyStep3Data, {
            studySettingDuration: 1440,
            unitTime: 'day',
            studyType: option.value,
          });
        } else {
          _.assign(setStateObject, {
            studySettingDuration: 30,
          });
          _.assign(updatedNewStudyStep3Data, {
            studySettingDuration: 1440 * 30,
            unitTime: 'day',
            studyType: option.value,
          });
        }

        updateReportDueDate(updatedNewStudyStep3Data, updatedNewStudyStep3Data.facility);
        const updateSetStateObject = setStudySetting(
          updatedNewStudyStep3Data,
          updatedNewStudyStep3Data.facility,
          true,
        );
        _.assign(setStateObject, updateSetStateObject);
        break;
      }
      case 'followOnStudyType': {
        if (option.value === STUDY_TYPES.HOLTER) {
          _.assign(setStateObject, { followOnStudyDuration: 3 });
          _.assign(updatedNewStudyStep3Data, {
            followOnStudyDuration: 1440 * 3,
            unitTimeFO: 'day',
          });
        } else {
          _.assign(setStateObject, {
            followOnStudyDuration: 1440 * 30,
          });
          _.assign(updatedNewStudyStep3Data, {
            followOnStudyDuration: 1440 * 30,
            unitTimeFO: 'day',
          });
        }

        updateReportDueDate(updatedNewStudyStep3Data, updatedNewStudyStep3Data.facility);
        const updateSetStateObject = setStudySetting(
          updatedNewStudyStep3Data,
          updatedNewStudyStep3Data.facility,
          true,
        );
        _.assign(setStateObject, updateSetStateObject);

        break;
      }
      case 'unitTime': {
        const isFollowOnStudy = !_.isNil(updatedNewStudyStep3Data.followOnStudyType);

        if (value === 'hour') {
          _.assign(setStateObject, { studySettingDuration: 50 });
          updatedNewStudyStep3Data.studySettingDuration = 60 * 50;
        } else {
          const isHolterStudy = updatedNewStudyStep3Data.studyType === STUDY_TYPES.HOLTER;
          if (isHolterStudy) {
            _.assign(setStateObject, { studySettingDuration: 3 });
            updatedNewStudyStep3Data.studySettingDuration = 1440 * 3;
          } else if (isFollowOnStudy) {
            _.assign(setStateObject, { studySettingDuration: 1 });
            updatedNewStudyStep3Data.studySettingDuration = 1440;
          } else {
            _.assign(setStateObject, { studySettingDuration: 30 });
            updatedNewStudyStep3Data.studySettingDuration = 1440 * 30;
          }
        }

        updateReportDueDate(updatedNewStudyStep3Data, updatedNewStudyStep3Data.facility);
        _.assign(setStateObject, {
          reportDueDate: isFollowOnStudy
            ? updatedNewStudyStep3Data.followOnStudyReportDueDate
            : updatedNewStudyStep3Data.reportDueDate,
        });

        break;
      }
      case 'unitTimeFO': {
        const isFollowOnStudy = !_.isNil(updatedNewStudyStep3Data.followOnStudyType);

        if (value === 'hour') {
          _.assign(setStateObject, { followOnStudyDuration: 50 });
          updatedNewStudyStep3Data.followOnStudyDuration = 60 * 50;
        } else {
          const isHolterStudy = updatedNewStudyStep3Data.followOnStudyType === STUDY_TYPES.HOLTER;
          if (isHolterStudy) {
            _.assign(setStateObject, { followOnStudyDuration: 3 });
            updatedNewStudyStep3Data.followOnStudyDuration = 1440 * 3;
          } else {
            _.assign(setStateObject, { followOnStudyDuration: 30 });
            updatedNewStudyStep3Data.followOnStudyDuration = 1440 * 30;
          }
        }

        updateReportDueDate(updatedNewStudyStep3Data, updatedNewStudyStep3Data.facility);
        _.assign(setStateObject, {
          reportDueDate: isFollowOnStudy
            ? updatedNewStudyStep3Data.followOnStudyReportDueDate
            : updatedNewStudyStep3Data.reportDueDate,
        });

        break;
      }
      default: {
        break;
      }
    }

    _.assign(setStateObject, {
      [name]: option,
      newStudyStep3Data: updatedNewStudyStep3Data,
    });

    setState(setStateObject);
  };

  // === Physician Handlers ===
  /**
   * Builds the dropdown scroll handler that pages in more referring or interpreting physicians. The returned handler tracks the last scroll position per type and, when scrolling down past 80% with no request in flight and more pages left, appends the next page for the current search text to the options already loaded.
   *
   * @param type - Which physician list the handler pages.
   * @returns Scroll handler for that physician dropdown.
   */
  const createPhysicianScrollHandler =
    (type: 'referring' | 'interpreting') => (e: React.UIEvent<HTMLDivElement>) => {
      const isReferring = type === 'referring';
      const target = e.target as HTMLDivElement;
      const currentScrollTop = target.scrollTop;
      const scrollPercentage =
        ((currentScrollTop + target.offsetHeight) / target.scrollHeight) * 100;
      const isScrollingDown = currentScrollTop > lastScrollTop.current[type];
      lastScrollTop.current[type] = currentScrollTop;

      const loading = isReferring
        ? state.isLoadingReferringPhysician
        : state.isLoadingInterprettingPhysician;
      const hasMore = isReferring
        ? state.hasMoreReferringPhysicians
        : state.hasMoreInterprettingPhysicians;

      if (isScrollingDown && scrollPercentage > 80 && !loading && hasMore) {
        const loadingKey = isReferring
          ? 'isLoadingReferringPhysician'
          : 'isLoadingInterprettingPhysician';
        const searchText = isReferring
          ? state.searchReferringPhysician
          : state.searchInterprettingPhysician;
        const options = isReferring
          ? state.referringPhysiciansOptions
          : state.interprettingPhysiciansOptions;

        setState({ [loadingKey]: true });
        handleFetchPhysiciansWithSearch({
          type,
          facilityId: state.selectedFacility.value,
          searchText,
          isLoadMore: true,
          existingOptions: options,
        }).then((newState) => {
          setState({ ...newState, [loadingKey]: false });
        });
      }
    };

  /**
   * Debounces the referring physician search text by 500 ms before it reaches state. Clears the pending referring timeout and schedules a new one that writes searchReferringPhysician, which the physician search effect then reacts to.
   *
   * @param value - Search text typed into the referring physician dropdown.
   * @returns Nothing.
   */
  const handleSearchReferringPhysician = (value: string) => {
    clearTimeout(searchPhysicianTimeouts.current.referring);
    searchPhysicianTimeouts.current.referring = setTimeout(() => {
      setState({ searchReferringPhysician: value });
    }, 500);
  };

  /**
   * Debounces the interpreting physician search text by 500 ms before it reaches state. Clears the pending interpreting timeout and schedules a new one that writes searchInterprettingPhysician, which the physician search effect then reacts to.
   *
   * @param value - Search text typed into the interpreting physician dropdown.
   * @returns Nothing.
   */
  const handleSearchInterprettingPhysician = (value: string) => {
    clearTimeout(searchPhysicianTimeouts.current.interpreting);
    searchPhysicianTimeouts.current.interpreting = setTimeout(() => {
      setState({ searchInterprettingPhysician: value });
    }, 500);
  };

  const handleScrollReferringPhysician = createPhysicianScrollHandler('referring');
  const handleScrollInterprettingPhysician = createPhysicianScrollHandler('interpreting');

  /**
   * Commits a chosen device option as the study's device and syncs the settings that depend on it. Writes the device id into the step 3 data and the search box, reapplies the report due date and the default study settings when the device is a Biotres model, and opens the Protect Plus confirmation modal when the option is flagged Protect Plus.
   *
   * @param option - Device option picked in the device id dropdown.
   * @returns Nothing.
   */
  const updateDataDeviceId = (option: DeviceOption) => {
    const setStateObject = {};

    const updatedNewStudyStep3Data = { ...state.newStudyStep3Data };

    updatedNewStudyStep3Data.deviceId = option.value;

    /**
     * The full device record behind the device option just picked. Found in the loaded devices by matching deviceId against the option's value, so Biotres models can be detected.
     *
     * @returns The matching device record, or undefined.
     */
    const device = _.find(state.devices, (x) => x.deviceId === option.value);
    if (device && device.model === DEVICE.BIOTRES) {
      updateReportDueDate(updatedNewStudyStep3Data, updatedNewStudyStep3Data.facility);
      const updateSetStateObject = setStudySetting(
        updatedNewStudyStep3Data,
        updatedNewStudyStep3Data.facility,
        true,
      );
      _.assign(setStateObject, updateSetStateObject);
    }

    updatedNewStudyStep3Data.deviceId = option.value;

    if (option.isProtectPlus) {
      _.assign(setStateObject, {
        isOpenProtectPlusDeviceModal: true,
      });
    }

    _.assign(setStateObject, {
      deviceId: option,
      newStudyStep3Data: updatedNewStudyStep3Data,
      searchDeviceId: option.value,
    });

    setState(setStateObject);
  };

  /**
   * Validates a newly picked device before it becomes the study's device, under the page loading overlay. Opens the device error modal on a failed check, the firmware or auto event warning when a Biotres device cannot run the selected non-Holter study, or the returned device modal when the device is still flagged as sent back by a patient, and otherwise commits the selection while logging and reporting a failed lookup.
   *
   * @param name - Name of the device field being set.
   * @param value - Selected device id.
   * @param option - Selected device option, carrying its model, schema and capabilities.
   * @returns Promise that settles once the device is committed or a modal is opened.
   */
  const handleDeviceChange = async (name: string, value: string, option: DeviceOption) => {
    hasChangedDevice.current = true;

    loadingPageAction(LOADING_TYPES.LOADING);

    const { isOpenDeviceErrorModal, deviceErrorModalMessage } = checkDeviceError(option);
    if (isOpenDeviceErrorModal) {
      setState({
        isOpenDeviceErrorModal,
        deviceErrorModalMessage,
      });
      loadingPageAction();
      return;
    }

    if (option.model === DEVICE.BIOTRES) {
      const studyCondition =
        state.studyType &&
        ![STUDY_TYPES.HOLTER, STUDY_TYPES.EXTENDED_HOLTER].includes(state.studyType?.value);
      if (
        studyCondition &&
        !option?.canRunBiotresMCT &&
        state.selectedFacility?.status !== 'Test'
      ) {
        setState({ isOpenWarningAutoEventModal: true });
        loadingPageAction();
        return;
      }

      if (studyCondition && checkCanShowModal(option.lastSync.schema)) {
        setState({ isOpenWarningModal: true });
        loadingPageAction();
        return;
      }
    }

    try {
      const sentDevicePatientReturned = await fetchSentDevicePatientReturned(option.value);
      if (!_.isEmpty(sentDevicePatientReturned)) {
        const isClinicRole =
          _.intersection(meRedux.roles, [ROLE.CLINIC_TECHNICIAN, ROLE.CLINIC_PHYSICIAN]).length > 0;
        const isViewGotoFleetManagement = isClinicRole && !isWebView;
        setState({
          isOpenReturnedDeviceModal: true,
          returnedDeviceDataModal: {
            ...option,
            isViewGotoFleetManagement,
            patientReturnId: sentDevicePatientReturned?.id,
            studyId: sentDevicePatientReturned?.study?.id,
          },
        });
        loadingPageAction();
        return;
      }

      updateDataDeviceId(option);
    } catch (error) {
      console.error('Failed to fetch sent device patient returned: ', error);
      showMessageError({ content: t`Failed to check device ID` });
    } finally {
      loadingPageAction();
    }
  };

  /**
   * Stores the report due date the user picked, on the follow-on field when a follow-on study type is set and on the primary field otherwise, clearing the other one. Sets preventUpdatingReportDueDate when the date was cleared so later duration changes no longer recompute it.
   *
   * @param date - Report due date chosen in the date picker, or null when cleared.
   * @returns Nothing.
   */
  const onChangeReportDueDate = (date: Dayjs) => {
    const updatedNewStudyStep3Data = { ...state.newStudyStep3Data };
    const isFollowOnStudy = !_.isNil(updatedNewStudyStep3Data.followOnStudyType);
    if (isFollowOnStudy) {
      updatedNewStudyStep3Data.followOnStudyReportDueDate = date;
      updatedNewStudyStep3Data.reportDueDate = undefined;
    } else {
      updatedNewStudyStep3Data.reportDueDate = date;
      updatedNewStudyStep3Data.followOnStudyReportDueDate = undefined;
    }
    updatedNewStudyStep3Data.preventUpdatingReportDueDate = !date;
    setState({
      newStudyStep3Data: updatedNewStudyStep3Data,
      reportDueDate: date,
    });
  };

  /**
   * Creates a referring physician for the selected facility from the drawer's form and adds them to the dropdown. Formats the entered details, calls the create mutation, prepends the new option to the referring physician list, closes the drawer and refreshes the physician store for the stored facility, logging and showing the error when the call fails.
   *
   * @param referringPhysicianInfo - Referring physician details entered in the drawer.
   * @returns Promise that settles once the physician is added or the failure is reported.
   */
  const handleClickCreateReferringPhysician = async (
    referringPhysicianInfo: ReferringPhysician,
  ) => {
    try {
      setState({ isCreatingReferringPhysician: true });

      const formattedReferringPhysicianInfo =
        referringPhysicianInfoFormatter(referringPhysicianInfo);
      const user = await handleCreateReferringPhysician(
        formattedReferringPhysicianInfo,
        state.selectedFacility.value,
      );
      if (!user) {
        throw new Error('Failed to create referring physician');
      }
      const newPhysicianOption = {
        label: formatPhysicianOptionLabel({
          ...user,
          firstName: user.firstName ?? '',
          lastName: user.lastName ?? '',
          npi: user.npi ?? '',
          contact: user.contact ?? { fax: null, nightPhone: null, phone1: null },
        }),
        value: user.id,
      };
      const newReferringPhysiciansOptions = [
        newPhysicianOption,
        ...state.referringPhysiciansOptions,
      ];
      setState({
        referringPhysiciansOptions: newReferringPhysiciansOptions,
        isVisibleDrawer: false,
        isCreatingReferringPhysician: false,
      });

      const selectedFacilityLocalStorage = facilitySelection.selectedFacilityLocalStorage();
      fetchPhysiciansRequestAction(selectedFacilityLocalStorage.value);
    } catch (error) {
      console.error('Failed to create referring physician: ', error);
      showMessageError({ content: error instanceof Error ? error.message : '' });
      setState({ isCreatingReferringPhysician: false });
    }
  };

  /**
   * Handles the Back and Next buttons of step 3. Going back forwards a copy of the data with the physician fields flattened, while going next validates device id, ring id, physicians, study types, durations, report due date and clinician initials and checks that an ICOR device is not already in an active study, then either highlights the errors, opens the functional test or patient email modal, or forwards the completed step 3 data to the wizard.
   *
   * @param isNextClicked - True when Next was pressed, false for Back.
   * @returns Promise that settles once validation ran and the wizard was notified.
   */
  const onClickBottomButtons = async (isNextClicked: boolean) => {
    if (isNextClicked) {
      setIsLoading(true);

      const errorObject = {};

      // *: Comment below check to bypass no device case
      if (!state.isDeviceAssignmentPending && !state.isBiofluxDirect) {
        if (!state.newStudyStep3Data.deviceId) {
          _.assign(errorObject, { deviceId: 'Invalid value' });
        } else if (isIcorDevice) {
          const isIcorStudyActive = await fetchIsIcorStudyActive(state.newStudyStep3Data.deviceId);
          if (isIcorStudyActive) {
            _.assign(errorObject, {
              deviceId: 'This device is currently assigned to an active study.',
            });
          }
        }

        if (state.newStudyStep3Data.ringId && state.newStudyStep3Data.ringId.length > 6) {
          _.assign(errorObject, { ringId: 'Invalid value' });
        }
      }

      if (!state.referringPhysician?.value) {
        _.assign(errorObject, { referringPhysician: 'Invalid value' });
      }

      if (!state.interprettingPhysician?.value) {
        _.assign(errorObject, { interprettingPhysician: 'Invalid value' });
      }

      if (isFollowOnStudyType) {
        if (!state.primaryStudyType) {
          _.assign(errorObject, { primaryStudyType: 'Invalid value' });
        }
      } else if (!state.studyType?.value) {
        _.assign(errorObject, { studyType: 'Invalid value' });
      }

      const studySettingDurationError = checkStudyDuration({
        studyDuration: state.newStudyStep3Data.studySettingDuration || 0,
        studyType: state.newStudyStep3Data.studyType,
        unitTime: state.newStudyStep3Data.unitTime,
        isHighSamplingRate: state.deviceId?.isHighSamplingRate,
      });
      if (studySettingDurationError) {
        _.assign(errorObject, {
          studySettingDuration: studySettingDurationError,
        });
      }

      if (isFollowOnStudyType && !state.newStudyStep3Data.followOnStudyType) {
        _.assign(errorObject, { followOnStudyType: 'Invalid value' });
      }

      if (isFollowOnStudyType) {
        const followOnStudyDurationError = checkStudyDuration({
          studyDuration: state.newStudyStep3Data.followOnStudyDuration || 0,
          studyType: state.newStudyStep3Data.followOnStudyType,
          unitTime: state.newStudyStep3Data.unitTimeFO,
          isHighSamplingRate: state.deviceId?.isHighSamplingRate,
        });
        if (followOnStudyDurationError) {
          _.assign(errorObject, {
            followOnStudyDuration: followOnStudyDurationError,
          });
        }
      }

      if (!state.isDeviceAssignmentPending && !state.isBiofluxDirect) {
        if (
          (!isFollowOnStudyType && !state.newStudyStep3Data.reportDueDate) ||
          (isFollowOnStudyType && !state.newStudyStep3Data.followOnStudyReportDueDate)
        ) {
          _.assign(errorObject, { reportDueDate: 'Invalid value' });
        }
      }

      if (!state.newStudyStep3Data.clinicianInitials) {
        _.assign(errorObject, { clinicianInitials: 'Invalid value' });
      }

      if (!_.isEmpty(errorObject)) {
        showMessageError({
          content: t`Some information is invalid. Please review the highlighted field(s).`,
        });
        setState({
          errorObject,
          isShowMoreInfo: false,
        });
        setIsLoading(false);
      } else if (
        state.isTest &&
        !_.includes(['MCT', 'CARDIAC EVENT'], state.newStudyStep3Data.studyType) &&
        (state.newStudyStep3Data.model === DEVICE.BIOFLUX ||
          !checkCanShowModal(state.deviceId.schema))
      ) {
        setState({ isOpenFunctionalTestModal: true });
        setIsLoading(false);
      } else if (
        (isMissingEmailWhenUseBiodirect || isMissingEmailWhenDeviceBiotresAndOutsideUS) &&
        !state.isDeviceAssignmentPending
      ) {
        setState({ isOpenPatientEmailModal: true });
        setIsLoading(false);
      } else {
        _.assign(state.newStudyStep3Data, {
          referringPhysician: state.referringPhysician?.value,
          referringPhysicianFirstName: state.referringPhysicianFirstName,
          referringPhysicianLastName: state.referringPhysicianLastName,
          interprettingPhysician: state.interprettingPhysician?.value,
          interprettingPhysicianFirstName: state.interprettingPhysicianFirstName,
          interprettingPhysicianLastName: state.interprettingPhysicianLastName,
        });
        if (isFollowOnStudyType) {
          _.assign(state.newStudyStep3Data, {
            studyType: state.primaryStudyType!.value,
            followOnStudyType: state.followOnStudyType.value,
          });
        } else {
          // errorObject is empty here, and the earlier validation above only clears it
          // when !isFollowOnStudyType and state.studyType?.value is truthy
          _.assign(state.newStudyStep3Data, {
            studyType: state.studyType!.value,
          });
        }
        handleClick(
          {
            step3: state.newStudyStep3Data,
          },
          true,
        );
        setIsLoading(false);
      }
    } else {
      const step3 = _.cloneDeep(state.newStudyStep3Data);
      _.assign(step3, {
        referringPhysician: state.referringPhysician?.value,
        referringPhysicianFirstName: state.referringPhysicianFirstName,
        referringPhysicianLastName: state.referringPhysicianLastName,
        interprettingPhysician: state.interprettingPhysician?.value,
        interprettingPhysicianFirstName: state.interprettingPhysicianFirstName,
        interprettingPhysicianLastName: state.interprettingPhysicianLastName,
      });
      handleClick({ step3 }, false);
      setIsLoading(false);
    }
  };

  /**
   * Loads the full facility record, including the study settings the form defaults from. Awaits the facility query and, on failure, logs the error and returns null so the caller keeps the facility it already has.
   *
   * @param facilityId - Id of the facility to load.
   * @returns Promise of the full facility record, or null on failure.
   */
  const handleFetchFacilityFull = async (facilityId: string) => {
    try {
      return await fetchFacilityFull(facilityId);
    } catch (error) {
      console.error("Failed to fetch user's facilty: ", error);
      return null;
    }
  };

  /**
   * Loads the devices assignable to a new study at one facility, asking for up to 100 online, active, not-in-study and non-renewal devices of the currently selected model, sorted by device id descending. Removes devices already running a study, and logs the failure and returns an empty list when the query throws.
   *
   * @param facilityId - Id of the facility whose devices are loaded.
   * @returns Promise of the assignable device records, empty on failure.
   */
  const handleFetchDevices = async (facilityId: string) => {
    try {
      const queryFilter = {
        isInStudy: false,
        status: DEVICE_STATUS.ONLINE,
        sortBy: 'deviceId',
        sortOrder: 'desc',
        facilityId,
        model: state.newStudyStep3Data.model,
        isRenewal: false,
        additionalStatus: 'Active',
      };
      // GraphQL's device shape has nullable sub-fields the hand-written Device type declares required
      const devices = _.compact(
        await fetchDevicesForStudyCreation(queryFilter, 100),
      ) as unknown as Device[];
      _.remove(devices, (x) => x.isRunningStudy);
      return devices;
    } catch (error) {
      console.error('Failed to fetch call center devices: ', error);
      return [];
    }
  };

  // Removed: handleFetchPhysicians - now using handleFetchPhysiciansWithSearch with limit 10

  /**
   * Loads a facility's assignable devices and builds the option list for the device id dropdown. Converts the fetched devices with handleConvertDeviceData using whether the facility status is Test, which tags RMA devices and disables renewal ones, and returns both the raw devices and the options.
   *
   * @param facilityId - Id of the facility whose devices are loaded.
   * @returns Promise of the device records together with their dropdown options.
   */
  const fetchDevices = async (facilityId: string) => {
    // *: Test facility:  display RMA tag and disabled renewal device
    // *: Normal facility: disable RMA and renewal devices

    const devices = await handleFetchDevices(facilityId);
    const isFacilityTest = facilityStatus.current === 'Test';
    const devicesOptions = handleConvertDeviceData({
      devices,
      isFacilityTest,
    });
    return {
      devices,
      devicesOptions,
    };
  };

  /**
   * Seeds step 3 of the new study wizard from the incoming default data, the facility stored in local storage and the facility's own settings. Fetches the referring and interpreting physicians together with the full facility, preselects facility, device, study types and physicians (self-interpret for clinic physicians), loads the facility's devices, applies the default study settings and duration alerts, and raises the escalation popup while blocking Next when the facility is 60 days in arrears.
   *
   * @returns Promise that settles once the seeded state and loading flags are written.
   */
  const createInitialData = async () => {
    setIsChangingFacility(true);

    const updatedNewStudyStep3Data = { ...state.newStudyStep3Data };
    _.merge(updatedNewStudyStep3Data, defaultData);

    const selectedFacilityLocalStorage = facilitySelection.selectedFacilityLocalStorage();
    const notSelectAllFacilities = selectedFacilityLocalStorage.value !== GETTEXT_ALL_FACILITIES;

    const existedFacility = !_.isEmpty(defaultData.facility)
      ? _.find(state.formattedSupportFacilities, (x) => x.value === defaultData.facility.id)
      : undefined;

    let referringResult;
    let interprettingResult;
    let facility;

    if (existedFacility) {
      [referringResult, interprettingResult, facility] = await Promise.all([
        handleFetchPhysiciansWithSearch({
          type: 'referring',
          facilityId: existedFacility.value,
          searchText:
            defaultData.referringPhysicianFirstName || defaultData.referringPhysicianLastName
              ? _.join(
                  _.filter(
                    [
                      defaultData.referringPhysicianFirstName,
                      defaultData.referringPhysicianLastName,
                    ],
                    Boolean,
                  ),
                  ' ',
                )
              : '',
        }),
        handleFetchPhysiciansWithSearch({
          type: 'interpreting',
          facilityId: existedFacility.value,
          searchText:
            defaultData.interprettingPhysicianFirstName ||
            defaultData.interprettingPhysicianLastName
              ? _.join(
                  _.filter(
                    [
                      defaultData.interprettingPhysicianFirstName,
                      defaultData.interprettingPhysicianLastName,
                    ],
                    Boolean,
                  ),
                  ' ',
                )
              : auth.isClinicPhysician()
                ? meRedux.firstName || meRedux.lastName
                  ? _.join(_.filter([meRedux.firstName, meRedux.lastName], Boolean), ' ')
                  : ''
                : '',
        }),
        handleFetchFacilityFull(defaultData.facility.id),
      ]);
    } else {
      const facilityId = notSelectAllFacilities
        ? selectedFacilityLocalStorage.value
        : state.formattedSupportFacilities[0]?.value;
      [referringResult, interprettingResult, facility] = await Promise.all([
        handleFetchPhysiciansWithSearch({
          type: 'referring',
          facilityId,
          searchText: '',
        }),
        handleFetchPhysiciansWithSearch({
          type: 'interpreting',
          facilityId,
          searchText: '',
        }),
        handleFetchFacilityFull(facilityId),
      ]);
    }

    /**
     * The facility option the form starts with. Prefers the facility carried in the default data, otherwise the facility stored in local storage, falling back to the first support facility when the all-facilities sentinel is stored.
     *
     * @returns The facility option to preselect.
     */
    const selectedFacility =
      existedFacility ||
      (notSelectAllFacilities ? selectedFacilityLocalStorage : state.formattedSupportFacilities[0]);

    const setStateObject = {
      selectedFacility,
      isTest: selectedFacility.status === 'Test' && defaultData.isTest,
      isDeviceAssignmentPending: defaultData.isDeviceAssignmentPending,
      isBiofluxDirect: defaultData.isBiofluxDirect,
    };

    let isDisabledNext = false;
    /**
     * The full support facility record backing the currently selected facility option. Looked up in the support store's facilities by id against the selected option's value so its arrears can be checked.
     *
     * @returns The matching support facility record, or undefined.
     */
    const foundSelectedFacility = _.find(
      supportFacilitiesRedux,
      (x) => x.id === selectedFacility.value,
    );
    if (foundSelectedFacility) {
      if (foundSelectedFacility.arrears?.toString() === '60') {
        emitter.emit(EMITTER_CONSTANTS.SHOW_POPUP_ESCALATION, foundSelectedFacility.arrears);
        isDisabledNext = true;
      } else {
        isDisabledNext = false;
      }
    }

    if (facility) {
      // GraphQL's facility shape has nullable sub-fields the hand-written Facility type
      // declares required; facility is confirmed present by the guard above.
      updatedNewStudyStep3Data.facility = facility as unknown as Facility;
    }

    facilityStatus.current = selectedFacility?.status;
    const currentDeviceModelList = getDeviceModelList({
      isTest: setStateObject.isTest as boolean,
      isBiofluxDirect: setStateObject.isBiofluxDirect as boolean,
      canUseIcor: setStateObject.selectedFacility?.canUseIcor,
    });
    /**
     * The device model entry matching the model held in the working step 3 data. Looked up in the model list computed for the current facility, functional-test flag and Bioflux Direct flag.
     *
     * @returns The matching device model entry, or undefined.
     */
    const foundDeviceType = _.find(
      currentDeviceModelList,
      (x) => x.value === updatedNewStudyStep3Data.model,
    );

    if (!foundDeviceType) {
      updatedNewStudyStep3Data.model = currentDeviceModelList[0].value;
    } else if (updatedNewStudyStep3Data.model === DEVICE.ICOR) {
      updateStepTitleBiofluxDirect(true);
    }

    const { devices, devicesOptions } = await fetchDevices(selectedFacility.value);
    _.assign(setStateObject, {
      devices,
      devicesOptions,
    });

    /**
     * The device option matching the device id in the incoming default data. Looked up in the device options built from the facility's devices, and undefined when that device is no longer selectable.
     *
     * @returns The matching device option, or undefined.
     */
    const deviceId = devicesOptions.find((x) => x.value === defaultData.deviceId);
    _.assign(setStateObject, { deviceId });
    if (deviceId) {
      hasChangedDevice.current = true;
      _.assign(setStateObject, {
        searchDeviceId: deviceId?.value ?? '',
      });
    }

    const defaultStudyType = defaultData.followOnStudyType
      ? _.find(STUDY_TYPE_OPTIONS, (x) => x.value === FOLLOW_ON_STUDY_TYPE)
      : _.find(STUDY_TYPE_OPTIONS, (x) => x.value === defaultData.studyType);
    /**
     * The study type option matching the follow-on study type carried in the incoming default data. Looked up in STUDY_TYPE_OPTIONS by value, and undefined when no option matches.
     *
     * @returns The matching follow-on study type option, or undefined.
     */
    const defaultFollowOnStudyType = _.find(
      STUDY_TYPE_OPTIONS,
      (x) => x.value === defaultData.followOnStudyType,
    );
    _.assign(setStateObject, {
      studyType: defaultStudyType,
      primaryStudyType: defaultData.followOnStudyType
        ? _.find(STUDY_TYPE_OPTIONS, (x) => x.value === defaultData.studyType)
        : PRIMARY_STUDY_TYPE_LIST[0],
      followOnStudyType: defaultFollowOnStudyType,
    });

    const referringPhysiciansOptions = (referringResult.referringPhysiciansOptions || []) as any[];
    const interprettingPhysiciansOptions = (interprettingResult.interprettingPhysiciansOptions ||
      []) as any[];

    /**
     * The referring physician option matching the referring physician id in the incoming default data. Looked up in the freshly fetched referring physician options by value, and undefined when that physician is not in the list.
     *
     * @returns The matching referring physician option, or undefined.
     */
    const defaultReferringPhysician = _.find(
      referringPhysiciansOptions,
      (x) => x.value === defaultData.referringPhysician,
    );
    if (defaultReferringPhysician) {
      updatedNewStudyStep3Data.referringPhysician = defaultReferringPhysician.value;
      _.assign(setStateObject, {
        referringPhysician: defaultReferringPhysician,
        referringPhysicianFirstName: defaultReferringPhysician.firstName,
        referringPhysicianLastName: defaultReferringPhysician.lastName,
      });
    }

    let defaultInterpretingPhysician;
    if (defaultData.interprettingPhysician) {
      defaultInterpretingPhysician = _.find(
        interprettingPhysiciansOptions,
        (x) => x.value === defaultData.interprettingPhysician,
      );
    } else if (auth.isClinicPhysician()) {
      // Clinic Physician only create a study with self interpret
      defaultInterpretingPhysician = _.find(
        interprettingPhysiciansOptions,
        (x) => x.value === auth.userId(),
      );
    } else {
      defaultInterpretingPhysician = null;
    }
    if (defaultInterpretingPhysician) {
      updatedNewStudyStep3Data.interprettingPhysician = defaultInterpretingPhysician.value;
      _.assign(setStateObject, {
        interprettingPhysician: defaultInterpretingPhysician,
        interprettingPhysicianFirstName: defaultInterpretingPhysician.firstName,
        interprettingPhysicianLastName: defaultInterpretingPhysician.lastName,
      });
    }

    const updateSetStateObject: {
      studySettingDuration?: number;
      followOnStudyDuration?: number;
      // facility may be null when handleFetchFacilityFull's fetch failed; setStudySetting's
      // Facility shape is otherwise satisfied by the GraphQL result at this call site.
    } = setStudySetting(updatedNewStudyStep3Data, facility as unknown as Facility);

    const isShowedStudyDurationAlert =
      state.newStudyStep3Data.isShouldShowStudyDurationAlert &&
      defaultData.studyType !== STUDY_TYPES.HOLTER &&
      typeof updateSetStateObject.studySettingDuration === 'number' &&
      updateSetStateObject.studySettingDuration < 10;
    _.assign(setStateObject, { isShowedStudyDurationAlert });

    if (defaultData.followOnStudyType) {
      const isShowedFOStudyDurationAlert =
        state.newStudyStep3Data.isShouldShowFOStudyDurationAlert &&
        defaultData.followOnStudyType !== STUDY_TYPES.HOLTER &&
        typeof updateSetStateObject.followOnStudyDuration === 'number' &&
        updateSetStateObject.followOnStudyDuration < 10;
      _.assign(setStateObject, { isShowedFOStudyDurationAlert });
    }

    _.assign(setStateObject, {
      ...updateSetStateObject,
      newStudyStep3Data: updatedNewStudyStep3Data,
      // *: Set 10 items from fetch
      ...referringResult,
      ...interprettingResult,
    });

    setState(setStateObject);
    setIsLoadedData(true);
    setIsChangingFacility(false);
    setIsDisabledNext(isDisabledNext);
  };

  /**
   * Shows or hides one of the two device compatibility warnings, the firmware one for type 'firmware' and the auto event detection one otherwise. Flips that modal's flag and clears the selected study type in both the state and the step 3 data, since the device cannot run it.
   *
   * @param type - Which warning to toggle; 'firmware' selects the firmware modal.
   * @returns Nothing.
   */
  const toggleDisplayWarningModal = (type: string) => {
    const newStateModal =
      type === 'firmware'
        ? { isOpenWarningModal: !state.isOpenWarningModal }
        : { isOpenWarningAutoEventModal: !state.isOpenWarningAutoEventModal };

    setState({
      ...newStateModal,
      studyType: null,
      // cleared pending a new selection; primaryStudyType is otherwise always a selected value
      primaryStudyType: null,
      newStudyStep3Data: {
        ...state.newStudyStep3Data,
        studyType: '',
      },
    });
  };

  /**
   * Shows or hides the device error modal. Flips isOpenDeviceErrorModal and blanks the stored message so a stale error is not shown again.
   *
   * @returns Nothing.
   */
  const toggleDisplayDeviceErrorModal = () => {
    setState({
      isOpenDeviceErrorModal: !state.isOpenDeviceErrorModal,
      deviceErrorModalMessage: '',
    });
  };

  /**
   * Dismisses the device error modal and sends the user back to device selection after a device
   * change. Toggles the modal off and, when the device was just changed, clears that flag and
   * focuses and opens the device id select.
   *
   * @returns Nothing.
   */
  const handleRightButtonClickDeviceErrorModal: () => void = () => {
    toggleDisplayDeviceErrorModal();
    if (hasChangedDevice.current) {
      hasChangedDevice.current = false;
      if (deviceIdSelectRef.current) {
        deviceIdSelectRef.current.focusAndOpenSelectInput();
      }
    }
  };

  /**
   * Switches the study's facility and reloads everything scoped to it. Does nothing when the facility is unchanged, otherwise clears the physician options, searches and scroll positions, refetches both physician lists and the full facility, drops a selected physician who does not practise there, reloads the devices, downgrades ICOR to Bioflux and turns Bioflux Direct off when unsupported, reapplies the study settings and blocks Next at 60 days of arrears.
   *
   * @param name - Name of the facility field being set.
   * @param value - Id of the newly selected facility.
   * @param option - Selected facility option, carrying its status.
   * @returns Promise that settles once the facility-scoped state is reloaded.
   */
  const onChangeFacility = async (name: string, value: string, option: FacilityOption) => {
    if ((state as any)[name] !== value) {
      setIsChangingFacility(true);

      const setStateObject = { [name]: option };
      clearTimeout(searchPhysicianTimeouts.current.referring);
      clearTimeout(searchPhysicianTimeouts.current.interpreting);
      setState({
        searchDeviceId: '',
        // *: Clear physician options when changing facility
        referringPhysiciansOptions: [],
        interprettingPhysiciansOptions: [],
        searchReferringPhysician: '',
        searchInterprettingPhysician: '',
        hasMoreReferringPhysicians: true,
        hasMoreInterprettingPhysicians: true,
      });
      // Reset scroll positions
      lastScrollTop.current.referring = 0;
      lastScrollTop.current.interpreting = 0;

      let isDisabledNext = false;
      /**
       * The full support facility record for the facility just picked. Looked up in the support store's facilities by id so its arrears value can be checked.
       *
       * @returns The matching support facility record, or undefined.
       */
      const foundSelectedFacility = _.find(supportFacilitiesRedux, (x) => x.id === value);
      if (foundSelectedFacility) {
        if (foundSelectedFacility.arrears?.toString() === '60') {
          emitter.emit(EMITTER_CONSTANTS.SHOW_POPUP_ESCALATION, foundSelectedFacility.arrears);
          isDisabledNext = true;
        } else {
          isDisabledNext = false;
        }
      }

      const facilityId = value;
      const [referringResult, interprettingResult, facility] = await Promise.all([
        handleFetchPhysiciansWithSearch({
          type: 'referring',
          facilityId,
          searchText: '',
        }),
        handleFetchPhysiciansWithSearch({
          type: 'interpreting',
          facilityId,
          searchText: '',
        }),
        handleFetchFacilityFull(facilityId),
      ]);

      const updatedNewStudyStep3Data = { ...state.newStudyStep3Data };

      if (facility) {
        // GraphQL's facility shape has nullable sub-fields the hand-written Facility type
        // declares required; facility is confirmed present by the guard above.
        updatedNewStudyStep3Data.facility = facility as unknown as Facility;
      }

      /**
       * The referring physician option in the newly fetched facility list that matches the currently selected physician. Undefined when that physician does not practise at the new facility, which clears the selection.
       *
       * @returns The still-valid referring physician option, or undefined.
       */
      const defaultReferringPhysician = _.find(
        referringResult.referringPhysiciansOptions as any[],
        (x) => x.value === state.referringPhysician?.value,
      );
      if (!defaultReferringPhysician) {
        updatedNewStudyStep3Data.referringPhysician = null;
        _.assign(setStateObject, { referringPhysician: null });
      }

      /**
       * The interpreting physician option in the newly fetched facility list that matches the currently selected physician. Undefined when that physician does not practise at the new facility, which clears the selection.
       *
       * @returns The still-valid interpreting physician option, or undefined.
       */
      const defaultInterpretingPhysician = _.find(
        interprettingResult.interprettingPhysiciansOptions as any[],
        (x) => x.value === state.interprettingPhysician?.value,
      );
      if (!defaultInterpretingPhysician) {
        updatedNewStudyStep3Data.interprettingPhysician = null;
        _.assign(setStateObject, { interprettingPhysician: null });
      }

      facilityStatus.current = option?.status;

      setIsLoadingDevice(true);
      const { devices, devicesOptions } = await fetchDevices(facilityId);
      _.assign(setStateObject, {
        devices,
        devicesOptions,
      });
      setIsLoadingDevice(false);

      if (!facility?.canUseIcor && updatedNewStudyStep3Data.model === DEVICE.ICOR) {
        updatedNewStudyStep3Data.model = DEVICE.BIOFLUX;
      }

      if (state.isBiofluxDirect) {
        if (!facility?.canUseBiofluxDirect) {
          updatedNewStudyStep3Data.deviceId = null;
          updatedNewStudyStep3Data.isBiofluxDirect = false;
          _.assign(setStateObject, {
            isBiofluxDirect: false,
            deviceId: null,
          });
          updateStepTitleBiofluxDirect(false);
        }
      } else if (!state.isDeviceAssignmentPending) {
        updatedNewStudyStep3Data.deviceId = null;
        _.assign(setStateObject, { deviceId: null });
      }

      // facility may be null when handleFetchFacilityFull's fetch failed; setStudySetting's
      // Facility shape is otherwise satisfied by the GraphQL result at this call site.
      const updateSetStateObject = setStudySetting(
        updatedNewStudyStep3Data,
        facility as unknown as Facility,
      );
      _.assign(setStateObject, {
        ...updateSetStateObject,
        newStudyStep3Data: updatedNewStudyStep3Data,
        // *: Set 10 items from fetch
        ...referringResult,
        ...interprettingResult,
      });

      setState(setStateObject);
      setIsChangingFacility(false);
      setIsDisabledNext(isDisabledNext);
    }
  };

  /**
   * Closes the missing patient email modal without collecting an address. Sets isOpenPatientEmailModal to false.
   *
   * @returns Nothing.
   */
  const onClickCancelPatientEmailModal = () => {
    setState({ isOpenPatientEmailModal: false });
  };

  /**
   * Closes the returned device modal without marking the device as received. Sets isOpenReturnedDeviceModal to false.
   *
   * @returns Nothing.
   */
  const onClickCancelReturnedDeviceModal = () => {
    setState({ isOpenReturnedDeviceModal: false });
  };

  /**
   * Marks the device a patient returned as received and then assigns it to the study. Sends the patient return id to the mark-returned mutation and commits the device option stripped of its modal-only fields, logging and showing an error on failure while always clearing the modal's saving state and data.
   *
   * @returns Promise that settles once the device is assigned or the failure is reported.
   */
  const onClickConfirmReturnedDeviceModal = async () => {
    try {
      setState({ isSavingReturnedDeviceModal: true });
      const input = {
        ids: [state.returnedDeviceDataModal?.patientReturnId],
      };
      await handleMarkDevicePatientReturned(input);
      updateDataDeviceId(
        _.omit(_.cloneDeep(state.returnedDeviceDataModal), [
          'isViewGotoFleetManagement',
          'patientReturnId',
          'studyId',
        ]) as unknown as DeviceOption,
      );
    } catch (error) {
      console.error('Failed to mark device patient returned: ', error);
      showMessageError({ content: t`Failed to mark device patient returned` });
    } finally {
      setState({
        isSavingReturnedDeviceModal: false,
        isOpenReturnedDeviceModal: false,
        returnedDeviceDataModal: {},
      });
    }
  };

  /**
   * Acknowledges the functional test notice shown before a test study is created. Sets isOpenFunctionalTestModal to false.
   *
   * @returns Nothing.
   */
  const onClickOkayFunctionalTest = () => {
    setState({ isOpenFunctionalTestModal: false });
  };

  /**
   * Rejects the Protect Plus device that was selected and closes its confirmation modal. Clears the device id in both the state and the step 3 data copy so the study is left without a device.
   *
   * @returns Nothing.
   */
  const onClickCancelProtectPlusDevice = () => {
    const updatedNewStudyStep3Data = { ...state.newStudyStep3Data };
    updatedNewStudyStep3Data.deviceId = null;
    setState({
      deviceId: null,
      newStudyStep3Data: updatedNewStudyStep3Data,
      isOpenProtectPlusDeviceModal: false,
    });
  };

  /**
   * Accepts the Protect Plus device that was selected by closing its confirmation modal. Sets isOpenProtectPlusDeviceModal to false and leaves the device selection in place.
   *
   * @returns Nothing.
   */
  const onClickConfirmProtectPlusDevice = () => {
    setState({ isOpenProtectPlusDeviceModal: false });
  };

  /**
   * Reloads the unfiltered device list of the selected facility and marks the options as the default set. Raises the device loading flag, fetches the facility's assignable devices and their options, stores both in state and lowers the flag again.
   *
   * @returns Promise that settles once the devices are stored.
   */
  const handleDefaultDevicesLoad = async () => {
    setIsLoadingDevice(true);
    const { devices, devicesOptions } = await fetchDevices(state.selectedFacility.value);
    setState({
      isDefaultDeviceOptions: true,
      devices,
      devicesOptions,
    });
    setIsLoadingDevice(false);
  };

  /**
   * Loads devices whose id matches the typed text at the selected facility for the current model, up to 100 sorted by device id descending. Empties the device lists first, converts the results with handleConvertDeviceData using whether the facility is a Test facility, and on failure logs the error and only clears the searching flag.
   *
   * @param deviceId - Device id text to filter the query by.
   * @returns Promise that settles once the matching devices are stored.
   */
  const handleDevicesLoad = async (deviceId: string) => {
    try {
      setState({
        devices: [],
        devicesOptions: [],
        deviceId: null,
      });

      const queryFilter = {
        sortBy: 'deviceId',
        sortOrder: 'desc',
        facilityId: state.selectedFacility.value,
        model: state.newStudyStep3Data.model,
        deviceId,
      };
      // GraphQL's device shape has nullable sub-fields the hand-written Device type declares required
      const devices = _.compact(
        await fetchDevicesForStudyCreation(queryFilter, 100),
      ) as unknown as Device[];
      const isFacilityTest = facilityStatus.current === 'Test';
      const devicesOptions = handleConvertDeviceData({
        devices,
        isFacilityTest,
      });
      setState({
        isSearchingDevice: false,
        devices,
        devicesOptions,
      });
    } catch (error) {
      console.error('Failed to fetch call center devices: ', error);
      setState({ isSearchingDevice: false });
    }
  };

  /**
   * Starts a device id search from the user's typing and drops any device already selected. Swallows the callback once when it follows the dropdown closing or a programmatic device change, otherwise stores the search text, marks the options as no longer the default set and raises the searching flag.
   *
   * @param searchDeviceId - Device id text typed into the dropdown.
   * @returns Nothing.
   */
  const handleDeviceIdSearch = (searchDeviceId: string) => {
    if (hasClosedDeviceIdDropdown.current) {
      hasClosedDeviceIdDropdown.current = false;
      return;
    }

    if (hasChangedDevice.current) {
      hasChangedDevice.current = false;
      return;
    }

    const updatedNewStudyStep3Data = { ...state.newStudyStep3Data };
    updatedNewStudyStep3Data.deviceId = null;
    setState({
      isSearchingDevice: true,
      searchDeviceId,
      isDefaultDeviceOptions: false,
      deviceId: null,
      newStudyStep3Data: updatedNewStudyStep3Data,
    });
  };

  /**
   * Records that the device id dropdown has just been closed so the search callback it fires afterwards can be ignored. Stores the negated open flag in the hasClosedDeviceIdDropdown ref.
   *
   * @param open - Whether the device id dropdown is now open.
   * @returns Nothing.
   */
  const handleDeviceIdDropdownVisibleChange = (open: boolean) => {
    hasClosedDeviceIdDropdown.current = !open;
  };

  /**
   * Computes the earliest report due date the form may accept, as the epoch millisecond end of the day the recording finishes. Converts the study duration, plus the follow-on study duration when a follow-on study type is set, from hours to whole days where needed and adds the total to today.
   *
   * @returns Epoch milliseconds of the end of the last recording day.
   */
  const getReportDueDateBelowLimit = () => {
    const isFollowOnStudy = !_.isNil(state.newStudyStep3Data.followOnStudyType);
    if (isFollowOnStudy) {
      const isUnitHours = state.newStudyStep3Data.unitTime === 'hour';
      const isUnitHoursFO = state.newStudyStep3Data.unitTimeFO === 'hour';
      let totalDuration = 0;
      let studyDuration = Number(state.studySettingDuration);
      let followOnStudyDuration = Number(state.followOnStudyDuration);
      if (isUnitHours) {
        studyDuration = Math.ceil(studyDuration / 24);
      }
      if (isUnitHoursFO) {
        followOnStudyDuration = Math.ceil(followOnStudyDuration / 24);
      }
      totalDuration = studyDuration + followOnStudyDuration;
      return dayjs().add(totalDuration, 'days').endOf('day').valueOf();
    }

    const isUnitHours = state.newStudyStep3Data.unitTime === 'hour';
    let studyDuration = Number(state.studySettingDuration);
    if (isUnitHours) {
      studyDuration = Math.ceil(studyDuration / 24);
    }
    return dayjs().add(studyDuration, 'days').endOf('day').valueOf();
  };

  /**
   * Applies a study pricing update pushed over the socket to the auto event detection capability of the loaded devices. Finds the first loaded device listed in the message's appliedDeviceIds, sets its canRunBiotresMCT from autoEventDetection, rebuilds the dropdown options, re-points the selected device at its refreshed option and closes the auto event warning modal.
   *
   * @param msg - Pricing message listing the affected device ids and the auto event detection flag.
   * @returns Nothing.
   */
  const handleStudyPricingUpdatedSocket = (msg: StudyPricingUpdatedSocketMessage) => {
    if (msg.appliedDeviceIds) {
      /**
       * The index of the first loaded device covered by the pricing update message. Found by testing each device's id against the message's appliedDeviceIds, and -1 when none match.
       *
       * @returns Index of the affected device, or -1.
       */
      const deviceNeedUpdate = _.findIndex(state.devices, (device) =>
        msg.appliedDeviceIds.includes(device.deviceId),
      );
      if (deviceNeedUpdate >= 0) {
        const newDevicesList = _.cloneDeep(state.devices);
        let newDeviceId = _.cloneDeep(state.deviceId);

        newDevicesList[deviceNeedUpdate].canRunBiotresMCT = !!msg.autoEventDetection;
        const isFacilityTest = facilityStatus.current === 'Test';
        const devicesOptions = handleConvertDeviceData({
          devices: newDevicesList,
          isFacilityTest,
        });
        if (state.deviceId && state.deviceId.value === newDevicesList[deviceNeedUpdate].deviceId) {
          newDeviceId = devicesOptions.find((device) => device.value === state.deviceId.value);
        }
        setState({
          devices: newDevicesList,
          devicesOptions,
          deviceId: newDeviceId,
          isOpenWarningAutoEventModal: false,
        });
      }
    }
  };

  /**
   * Keeps the device dropdown and the selected device consistent when a device event arrives over the socket. While the default option list is shown it refetches the facility's devices and either re-points the selection at the refreshed option or clears it when the device is gone; otherwise it clears the selection only when the message reports that exact device going offline.
   *
   * @param msg - Socket message carrying the device id and its new status.
   * @returns Promise that settles once the device state is reconciled.
   */
  const handleDeviceEventSocket = async (msg: DeviceEventSocketMessage) => {
    if (state.isDefaultDeviceOptions) {
      const { devices, devicesOptions } = await fetchDevices(state.selectedFacility.value);
      const setStateObject = {
        devices,
        devicesOptions,
      };
      if (state.deviceId && !state.isDeviceAssignmentPending && !state.isBiofluxDirect) {
        /**
         * The refreshed device option matching the currently selected device id. Looked up in the device options rebuilt after a device event, and undefined when that device is no longer selectable.
         *
         * @returns The refreshed device option, or undefined.
         */
        const newDeviceId = devicesOptions.find(
          (deviceOption) => deviceOption.value === state.deviceId.value,
        );
        const updatedNewStudyStep3Data = { ...state.newStudyStep3Data };
        if (newDeviceId) {
          updatedNewStudyStep3Data.deviceId = newDeviceId.value;
          _.assign(setStateObject, {
            deviceId: newDeviceId,
            newStudyStep3Data: updatedNewStudyStep3Data,
          });
        } else {
          updatedNewStudyStep3Data.deviceId = null;
          _.assign(setStateObject, {
            deviceId: null,
            searchDeviceId: '',
            newStudyStep3Data: updatedNewStudyStep3Data,
          });
        }
      }
      setState(setStateObject);
    } else if (state.deviceId && !state.isDeviceAssignmentPending && !state.isBiofluxDirect) {
      const shouldUpdate = msg.deviceId === state.deviceId.value;
      if (!shouldUpdate) {
        return;
      }

      if (msg.status === DEVICE_STATUS.OFFLINE) {
        const updatedNewStudyStep3Data = { ...state.newStudyStep3Data };
        updatedNewStudyStep3Data.deviceId = null;
        setState({
          deviceId: null,
          searchDeviceId: '',
          newStudyStep3Data: updatedNewStudyStep3Data,
        });
      }
    }
  };

  useImperativeHandle(ref, () => ({
    handleNavigation: onClickBottomButtons,
  }));

  /**
   * The warning shown when the patient return visit date leaves too little time for the End of Use report. Totals the study and follow-on durations in days, compares the chosen due date against today plus that total plus three business days, and yields an empty string when there is enough time or no date is set.
   *
   * @returns The warning text, or an empty string.
   */
  const reportDueDateMessage = useMemo(() => {
    const isFollowStudy = !_.isNil(state.newStudyStep3Data.followOnStudyType);

    let totalDuration = 0;
    if (isFollowStudy) {
      const isUnitHoursFO = state.newStudyStep3Data.unitTimeFO === 'hour';
      totalDuration =
        Number(state.followOnStudyDuration || 0) + Number(state.studySettingDuration || 0);
      if (isUnitHoursFO) {
        totalDuration = Math.ceil(totalDuration / 24);
      }
    } else {
      const isUnitHours = state.newStudyStep3Data.unitTime === 'hour';
      totalDuration = (state.studySettingDuration || 0) as number;
      if (isUnitHours) {
        totalDuration = Math.ceil(totalDuration / 24);
      }
    }

    const patientReturnVisitDateEpoch = dayjs(
      isFollowStudy
        ? state.newStudyStep3Data.followOnStudyReportDueDate
        : state.newStudyStep3Data.reportDueDate,
    )
      .endOf('day')
      .valueOf();
    const hasEouReportDateEpoch = dayjs()
      .add(totalDuration, 'days')
      .businessDaysAdd(3)
      .endOf('day')
      .valueOf();

    const isShowReportDueDateMessage = patientReturnVisitDateEpoch < hasEouReportDateEpoch;
    return isShowReportDueDateMessage && state.reportDueDate
      ? t`The End of Use report may not be available at this time. The patient return visit date is within 3 business days of the end of the study.`
      : '';
  }, [
    state.studyType,
    state.reportDueDate,
    state.newStudyStep3Data,
    state.studySettingDuration,
    state.followOnStudyDuration,
  ]);

  useConstructor(() => {
    setIsLoadedData(false);
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    const bodyElement = document.querySelector('.app-body');
    if (bodyElement) {
      bodyElement.scroll(0, 0);
    }
  }, []);

  useEffect(() => {
    createInitialData();
  }, [defaultData]);

  useUpdateEffect(() => {
    if (!_.isEmpty(state.errorObject)) {
      setState({
        errorObject: {},
        isShowMoreInfo: false,
      });
    }
  }, [state.newStudyStep3Data]);

  // Reset isTest when hiding checkbox functional test
  useEffect(() => {
    if (state.selectedFacility?.status !== 'Test') {
      const setStateObject = {};
      _.assign(setStateObject, { isTest: false });
      setState(setStateObject);
    }
  }, [state.selectedFacility?.status]);

  useUpdateEffect(() => {
    handleDefaultDevicesLoad();
  }, [state.newStudyStep3Data.model]);

  useUpdateEffect(() => {
    if (!state.isDeviceAssignmentPending && !state.isBiofluxDirect) {
      handleDefaultDevicesLoad();
    }
  }, [state.isDeviceAssignmentPending, state.isBiofluxDirect]);

  useUpdateEffect(() => {
    if (fetchDeviceTimeout.current) {
      clearTimeout(fetchDeviceTimeout.current);
    }

    if (!state.searchDeviceId) {
      setState({
        isSearchingDevice: false,
        devices: [],
        devicesOptions: [],
      });
      return;
    }

    if (hasChangedDevice.current) {
      hasChangedDevice.current = false;
      return;
    }

    fetchDeviceTimeout.current = setTimeout(async () => {
      await handleDevicesLoad(state.searchDeviceId);
    }, 500);
  }, [state.searchDeviceId]);

  // === Referring Physician Search Effect ===
  useUpdateEffect(async () => {
    setState({
      isLoadingReferringPhysician: true,
      referringPhysiciansOptions: [],
      hasMoreReferringPhysicians: true,
    });
    lastScrollTop.current.referring = 0;

    const newState = await handleFetchPhysiciansWithSearch({
      type: 'referring',
      facilityId: state.selectedFacility.value,
      searchText: state.searchReferringPhysician,
    });

    setState({ ...newState, isLoadingReferringPhysician: false });
  }, [state.searchReferringPhysician]);

  // === Interpreting Physician Search Effect ===
  useUpdateEffect(async () => {
    setState({
      isLoadingInterprettingPhysician: true,
      interprettingPhysiciansOptions: [],
      hasMoreInterprettingPhysicians: true,
    });
    lastScrollTop.current.interpreting = 0;

    const newState = await handleFetchPhysiciansWithSearch({
      type: 'interpreting',
      facilityId: state.selectedFacility.value,
      searchText: state.searchInterprettingPhysician,
    });

    setState({ ...newState, isLoadingInterprettingPhysician: false });
  }, [state.searchInterprettingPhysician]);

  useEmitter(
    EMITTER_CONSTANTS.STUDY_PRICING_UPDATED,
    (msg: StudyPricingUpdatedSocketMessage) => {
      handleStudyPricingUpdatedSocket(msg);
    },
    [state],
  );

  useEmitter(EMITTER_CONSTANTS.DEVICE_EVENT, handleDeviceEventSocket, [
    state.selectedFacility?.value,
    state.newStudyStep3Data,
    state.deviceId,
    state.devicesOptions,
    state.isDefaultDeviceOptions,
    state.isDeviceAssignmentPending,
    state.isBiofluxDirect,
  ]);

  return {
    state,
    setState,
    hasChangedDevice,
    deviceIdSelectRef,
    searchPhysicianTimeouts,
    isCardioOne,
    deviceModelList,
    isIcorDevice,
    studyTypeOptions,
    isWebView,
    isFollowOnStudyType,
    canSelectDurationUnit,
    isSecondHolterStudy,
    isMissingEmailWhenUseBiodirect,
    isMissingEmailWhenDeviceBiotresAndOutsideUS,
    toggleDisplayCreateReferringPhysicianDrawer,
    toggleDisplaySampleReport,
    handleCloseStudyDurationAlert,
    handleCloseFOStudyDurationAlert,
    downloadSampleReport,
    handleAssignDeviceLaterChange,
    handleUseBiodirectChange,
    onChange,
    onChangeSelection,
    handleSearchReferringPhysician,
    handleSearchInterprettingPhysician,
    handleScrollReferringPhysician,
    handleScrollInterprettingPhysician,
    handleDeviceChange,
    onChangeReportDueDate,
    handleClickCreateReferringPhysician,
    onClickBottomButtons,
    toggleDisplayWarningModal,
    handleRightButtonClickDeviceErrorModal,
    onChangeFacility,
    onClickCancelPatientEmailModal,
    onClickCancelReturnedDeviceModal,
    onClickConfirmReturnedDeviceModal,
    onClickOkayFunctionalTest,
    onClickCancelProtectPlusDevice,
    onClickConfirmProtectPlusDevice,
    handleDeviceIdSearch,
    handleDeviceIdDropdownVisibleChange,
    getReportDueDateBelowLimit,
    reportDueDateMessage,
  };
};
